//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { Action } from 'redux';
import _ from 'underscore';

import * as collectionsApi from '@apis/collections/collectionsApi';
import { ICollectionFiltersDto } from '@apis/collections/ICollectionFiltersDto';
import { ICollectionSourcesDto } from '@apis/collections/ICollectionSourcesDto';
import * as gatewayInstagramApi from '@apis/gateway-instagram/gatewayInstagramApi';
import * as trackedSourcesApi from '@apis/tracked-sources/trackedSourcesApi';
import { ICuralateStore } from '@models/AppState';
import { getStreamsDrawerActions } from '@react-bridge/wrappers/ng-service-provider';
import { setCurrentStreamName } from '@streams-drawer/actions/streams-drawer-actions';
import { IStream } from '@streams-drawer/model/IStream';
import { IStreamSource } from '@streams-drawer/model/IStreamSource';
import { StreamsDrawerActionNames } from '@streams-drawer/model/StreamsDrawerActionNames';
import {
  MAX_TRACKING_TIMEOUT,
  StreamModalType,
} from '@streams-drawer/streams-drawer-constants';
import {
  ISelectStreamAction,
  ISetStreamsAction,
  ISetTrackingSourceTimeout,
  StreamDrawerActionType,
} from '@streams/actions/streamDrawerActions';
import { STREAM_CREATION_ACTIONS } from '@streams/constants/streamCreationActionConstants';
import {
  DEFAULT_STREAM_NAME,
  StreamSourceTypes,
  StreamExcludedContentSourceTypes,
} from '@streams/constants/streamCreationConstants';
import * as streamsConversions from '@streams/models/streamsConversions';
import { instagramHashtagSpecialCharacterRegex } from '@utils/stringUtils';
import { ReduxDispatch, ReduxGetState } from 'app/curalate.store';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import { getHasMultiHandleInstagramEnabled } from '@account-settings/redux/facebook-instagram/selectors/getMultiHandleFeature';
import {
  getBrandInstagramId,
  getBrandInstagramUsername,
} from '@streams/selectors/getBrandInstagramUserAndId';
import { getMultiHandleAccountsUserId } from '@streams/selectors/getMultiHandleAccountsUserId';

interface IAddSelectedSourceAction extends Action {
  type: STREAM_CREATION_ACTIONS.ADD_SELECTED_SOURCE;
  sourceLabel: string;
  sourceType: StreamSourceTypes;
  sourceValue: string;
}

interface IChangeStreamNameAction extends Action {
  type: STREAM_CREATION_ACTIONS.CHANGE_STREAM_NAME;
  streamName: string;
}

interface IChangeSourceTypeAction extends Action {
  type: STREAM_CREATION_ACTIONS.CHANGE_SOURCE_TYPE;
  sourceType: StreamSourceTypes;
}

interface IChangeExclusionSourceTypeAction extends Action {
  type: STREAM_CREATION_ACTIONS.CHANGE_EXCLUSION_SOURCE_TYPE;
  sourceType: StreamSourceTypes;
}

interface IChangeSourceValueAction extends Action {
  type: STREAM_CREATION_ACTIONS.CHANGE_SOURCE_VALUE;
  sourceValue: string;
}

interface IClearStateAction extends Action {
  type: STREAM_CREATION_ACTIONS.CLEAR_STATE;
}

interface IRemoveSourceAction extends Action {
  type: STREAM_CREATION_ACTIONS.REMOVE_SELECTED_SOURCE;
  source: IStreamSource;
}

interface ISetExistingStreamAction extends Action {
  type: STREAM_CREATION_ACTIONS.SET_EXISTING_STREAM;
  existingStream: IStream;
}

interface ISetIsLoadingSourcesAction extends Action {
  type: STREAM_CREATION_ACTIONS.SET_IS_LOADING_SOURCES;
  isLoadingSources: boolean;
}

interface ISetOutstandingSaveRequest extends Action {
  type: STREAM_CREATION_ACTIONS.SET_OUTSTANDING_SAVE_REQUEST;
  hasOutstandingSaveRequest: boolean;
}

interface ISetSelectedSourcesAction extends Action {
  type: STREAM_CREATION_ACTIONS.SET_SELECTED_SOURCES;
  sources: IStreamSource[];
}

interface ISetCreateStreamErrorAction extends Action {
  type: STREAM_CREATION_ACTIONS.SET_CREATE_STREAM_ERROR;
  createStreamError: string;
}

interface ISetSourceErrorAction extends Action {
  type: STREAM_CREATION_ACTIONS.SET_SOURCE_ERROR;
  sourceError: string;
}

interface ISetSourceTypeErrorAction extends Action {
  type: STREAM_CREATION_ACTIONS.SET_SOURCE_TYPE_ERROR;
  sourceTypeError: string;
}

interface ISetExclusionSourceTypeErrorAction extends Action {
  type: STREAM_CREATION_ACTIONS.SET_EXCLUSION_SOURCE_TYPE_ERROR;
  exclusionSourceTypeError: string;
}

interface ISetStreamNameErrorAction extends Action {
  type: STREAM_CREATION_ACTIONS.SET_STREAM_NAME_ERROR;
  streamNameError: string;
}

interface ISetSelectedExclusionSourcesAction extends Action {
  type: STREAM_CREATION_ACTIONS.SET_SELECTED_EXCLUDED_SOURCES;
  exclusionSources: IStreamSource[];
}

interface IAddExclusionSourceAction extends Action {
  type: STREAM_CREATION_ACTIONS.ADD_EXCLUSION_SOURCE;
  sourceLabel: string;
  sourceType: StreamSourceTypes;
  sourceValue: string;
}

interface IRemoveExclusionSourceAction extends Action {
  type: STREAM_CREATION_ACTIONS.REMOVE_EXCLUSION_SOURCE;
  exclusionSource: IStreamSource;
}

interface IChangeExclusionSourceValueAction extends Action {
  type: STREAM_CREATION_ACTIONS.CHANGE_EXCLUSION_SOURCE_VALUE;
  sourceValue: string;
}

interface ISetExclusionSourceErrorAction extends Action {
  type: STREAM_CREATION_ACTIONS.SET_EXCLUSION_SOURCE_ERROR;
  exclusionSourceError: string;
}

export type StreamActionType =
  | IAddSelectedSourceAction
  | IChangeStreamNameAction
  | IChangeSourceTypeAction
  | IChangeExclusionSourceTypeAction
  | IChangeSourceValueAction
  | IClearStateAction
  | IRemoveSourceAction
  | ISetExistingStreamAction
  | ISetIsLoadingSourcesAction
  | ISetOutstandingSaveRequest
  | ISetSelectedSourcesAction
  | ISetCreateStreamErrorAction
  | ISetSourceErrorAction
  | ISetSourceTypeErrorAction
  | ISetStreamNameErrorAction
  | ISetSelectedExclusionSourcesAction
  | IAddExclusionSourceAction
  | IRemoveExclusionSourceAction
  | IChangeExclusionSourceValueAction
  | ISetExclusionSourceErrorAction
  | ISetExclusionSourceTypeErrorAction
  | StreamDrawerActionType;

const addUserSource = async (
  dispatch: ReduxDispatch,
  getState: () => ICuralateStore,
  sourceValue,
) => {
  const hasAlreadyAddedSource =
    getState().streams.streamCreation.sourcesAdded.some((source) => {
      // Check the added sourceLabels without the @ symbol.
      return (
        source.sourceType === StreamSourceTypes.USER &&
        source.sourceLabel.replace('@', '') === sourceValue
      );
    });

  if (hasAlreadyAddedSource) {
    dispatch<ISetSourceErrorAction>({
      type: STREAM_CREATION_ACTIONS.SET_SOURCE_ERROR,
      sourceError: 'You have already added this user',
    });
    return;
  }

  if (!/^[0-9a-zA-Z_.]*$/.test(sourceValue)) {
    dispatch<ISetSourceErrorAction>({
      type: STREAM_CREATION_ACTIONS.SET_SOURCE_ERROR,
      sourceError:
        'Usernames can only contain letters, numbers, periods, and underscores',
    });
  } else {
    let userData;
    try {
      const isConnectClient = getState().session.client.isShareV2Vendor;

      if (isConnectClient) {
        // Connect clients hit the endpoint that uses a random graph api token
        userData = await gatewayInstagramApi.getBusinessUser(sourceValue);
      } else {
        // Normal clients hit the endpoint that uses the graph api token for the connected instagram account
        userData =
          await gatewayInstagramApi.getBusinessUserForClient(sourceValue);
      }
    } catch {
      // Request failed: Either a backend error
      // or the user can't be found on the business discovery api.
      userData = null;
    }

    if (userData && userData.instagramBusinessId) {
      dispatch<IAddSelectedSourceAction>({
        type: STREAM_CREATION_ACTIONS.ADD_SELECTED_SOURCE,
        sourceLabel: sourceValue,
        sourceType: StreamSourceTypes.USER,
        sourceValue: userData.instagramBusinessId,
      });
    } else {
      dispatch<ISetSourceErrorAction>({
        type: STREAM_CREATION_ACTIONS.SET_SOURCE_ERROR,
        sourceError:
          'You entered a username that is private, ' +
          'is not a business profile, or does not exist.',
      });
    }
  }
};

const addExclusionUserSource = async (
  dispatch: ReduxDispatch,
  getState: () => ICuralateStore,
  sourceValue,
) => {
  const hasAlreadyAddedSource =
    getState().streams.streamCreation.exclusionSourcesAdded.some((source) => {
      // Check the added sourceLabels without the @ symbol.
      return (
        source.sourceType === StreamSourceTypes.USER &&
        source.sourceLabel.replace('@', '') === sourceValue
      );
    });

  if (hasAlreadyAddedSource) {
    dispatch<ISetExclusionSourceErrorAction>({
      type: STREAM_CREATION_ACTIONS.SET_EXCLUSION_SOURCE_ERROR,
      exclusionSourceError: 'You have already excluded this user',
    });
  } else if (!/^[0-9a-zA-Z_.]*$/.test(sourceValue)) {
    dispatch<ISetExclusionSourceErrorAction>({
      type: STREAM_CREATION_ACTIONS.SET_EXCLUSION_SOURCE_ERROR,
      exclusionSourceError:
        'Usernames can only contain letters, numbers, periods, and underscores',
    });
  } else {
    dispatch<IAddExclusionSourceAction>({
      sourceLabel: sourceValue,
      sourceType: StreamSourceTypes.USER,
      sourceValue,
      type: STREAM_CREATION_ACTIONS.ADD_EXCLUSION_SOURCE,
    });
  }
};

const addHashtagSource = async (
  dispatch: ReduxDispatch,
  getState: () => ICuralateStore,
  sourceValue,
) => {
  const hasAlreadyAddedSource =
    getState().streams.streamCreation.sourcesAdded.some((source) => {
      return (
        source.sourceType === StreamSourceTypes.HASHTAG &&
        source.sourceValue?.toLowerCase() === sourceValue?.toLowerCase()
      );
    });
  if (hasAlreadyAddedSource) {
    dispatch<ISetSourceErrorAction>({
      type: STREAM_CREATION_ACTIONS.SET_SOURCE_ERROR,
      sourceError: 'You have already added this hashtag',
    });
  } else if (instagramHashtagSpecialCharacterRegex.test(sourceValue)) {
    dispatch<ISetSourceErrorAction>({
      type: STREAM_CREATION_ACTIONS.SET_SOURCE_ERROR,
      sourceError: 'Hashtags cannot contain special characters or spaces.',
    });
  } else {
    // using React Query hooks. We just need to add the hashtag to the sources.
    dispatch<IAddSelectedSourceAction>({
      sourceLabel: sourceValue,
      sourceType: StreamSourceTypes.HASHTAG,
      sourceValue,
      type: STREAM_CREATION_ACTIONS.ADD_SELECTED_SOURCE,
    });
  }
};

const addExclusionHashtagSource = async (
  dispatch: ReduxDispatch,
  getState: () => ICuralateStore,
  sourceValue,
) => {
  const hasAlreadyAddedSource =
    getState().streams.streamCreation.exclusionSourcesAdded.some((source) => {
      return (
        source.sourceType === StreamSourceTypes.HASHTAG &&
        source.sourceValue?.toLowerCase() === sourceValue?.toLowerCase()
      );
    });
  if (hasAlreadyAddedSource) {
    dispatch<ISetExclusionSourceErrorAction>({
      type: STREAM_CREATION_ACTIONS.SET_EXCLUSION_SOURCE_ERROR,
      exclusionSourceError: 'You have already excluded this hashtag',
    });
  } else if (instagramHashtagSpecialCharacterRegex.test(sourceValue)) {
    dispatch<ISetExclusionSourceErrorAction>({
      type: STREAM_CREATION_ACTIONS.SET_EXCLUSION_SOURCE_ERROR,
      exclusionSourceError:
        'Hashtags cannot contain special characters or spaces.',
    });
  } else {
    dispatch<IAddExclusionSourceAction>({
      sourceLabel: sourceValue,
      sourceType: StreamSourceTypes.HASHTAG,
      sourceValue,
      type: STREAM_CREATION_ACTIONS.ADD_EXCLUSION_SOURCE,
    });
  }
};

const addMentionsAndTagsSource = async (
  dispatch: ReduxDispatch,
  getState: () => ICuralateStore,
  multiHandleUsername?: string,
) => {
  const hasAlreadyAddedSource =
    getState().streams.streamCreation.sourcesAdded.some((source) => {
      return source.sourceType === StreamSourceTypes.MENTIONS_AND_TAGS;
    });

  const brandInstagramUsername = multiHandleUsername
    ? multiHandleUsername
    : getBrandInstagramUsername(getState());

  if (hasAlreadyAddedSource) {
    dispatch<ISetSourceErrorAction>({
      type: STREAM_CREATION_ACTIONS.SET_SOURCE_ERROR,
      sourceError: 'You have already added your mentions and tags',
    });
  } else {
    dispatch<IAddSelectedSourceAction>({
      type: STREAM_CREATION_ACTIONS.ADD_SELECTED_SOURCE,
      sourceLabel: `${brandInstagramUsername} Mentions & Tags`,
      sourceType: StreamSourceTypes.MENTIONS_AND_TAGS,
      sourceValue: multiHandleUsername ?? undefined,
    });
  }
};

// To show a progress bar in the streams drawer after we edit a new stream, we search through the list of tracked
// sources to find the latest that was tracked and pass the difference between that time and the max tracking interval
// into the store
export const setTrackingSourceTimeout = async (dispatch: ReduxDispatch) => {
  const trackedSources = await trackedSourcesApi.getAllTrackedSources();

  const allTrackedSources = _(trackedSources).chain().values().flatten();

  let trackingTimeout = 0;
  const now = Date.now();
  allTrackedSources.forEach((source) => {
    const timeSinceTracking = now - source.trackDate;
    const timeout = MAX_TRACKING_TIMEOUT - timeSinceTracking;
    if (timeout > trackingTimeout) {
      trackingTimeout = timeout;
    }
  });

  dispatch<ISetTrackingSourceTimeout>({
    type: StreamsDrawerActionNames.SET_TRACKING_SOURCE_TIMEOUT,
    trackingTimeout,
  });
};

export const streamCreationActions = {
  addSelectedSource:
    (multiHandleUsername?: string) =>
    async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      if (getState().streams.streamCreation.hasOutstandingSaveRequest) {
        return;
      }

      // TODO: Use a better name than "SaveRequest" to describe what is happening here
      dispatch(streamCreationActions.setOutstandingSaveRequest(true));

      if (getState().streams.streamCreation.errors.sourceError) {
        dispatch(streamCreationActions.clearSourceError());
      }

      const currentSource = getState().streams.streamCreation.sourceSelector;

      if (currentSource.sourceType === StreamSourceTypes.USER) {
        await addUserSource(dispatch, getState, currentSource.sourceValue);
      } else if (currentSource.sourceType === StreamSourceTypes.HASHTAG) {
        await addHashtagSource(dispatch, getState, currentSource.sourceValue);
      } else if (
        currentSource.sourceType === StreamSourceTypes.MENTIONS_AND_TAGS
      ) {
        await addMentionsAndTagsSource(dispatch, getState, multiHandleUsername);
      } else {
        dispatch<ISetSourceErrorAction>({
          type: STREAM_CREATION_ACTIONS.SET_SOURCE_ERROR,
          sourceError: 'You must choose a source type',
        });
      }

      dispatch(streamCreationActions.setOutstandingSaveRequest(false));
    },

  directAddSource:
    (sourceType: StreamSourceTypes, sourceValue?: string) =>
    async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      if (getState().streams.streamCreation.hasOutstandingSaveRequest) {
        return;
      }

      // TODO: Use a better name than "SaveRequest" to describe what is happening here
      dispatch(streamCreationActions.setOutstandingSaveRequest(true));

      if (sourceType === StreamSourceTypes.USER) {
        await addUserSource(dispatch, getState, sourceValue);
      } else if (sourceType === StreamSourceTypes.HASHTAG) {
        await addHashtagSource(dispatch, getState, sourceValue);
      } else if (sourceType === StreamSourceTypes.MENTIONS_AND_TAGS) {
        await addMentionsAndTagsSource(dispatch, getState);
      }

      dispatch(streamCreationActions.setOutstandingSaveRequest(false));
    },

  setSelectedSources(sources: IStreamSource[]): ISetSelectedSourcesAction {
    return {
      type: STREAM_CREATION_ACTIONS.SET_SELECTED_SOURCES,
      sources,
    };
  },

  removeSelectedSource(source: IStreamSource): IRemoveSourceAction {
    return {
      type: STREAM_CREATION_ACTIONS.REMOVE_SELECTED_SOURCE,
      source,
    };
  },

  setOutstandingSaveRequest(status: boolean): ISetOutstandingSaveRequest {
    return {
      type: STREAM_CREATION_ACTIONS.SET_OUTSTANDING_SAVE_REQUEST,
      hasOutstandingSaveRequest: status,
    };
  },

  changeSourceValue: (sourceValue: string) => {
    return async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      if (getState().streams.streamCreation.errors.sourceError) {
        dispatch(streamCreationActions.clearSourceError());
      }

      const cleanSourceValue = sourceValue.replace('@', '').replace('#', '');

      const changeSourceValueAction: IChangeSourceValueAction = {
        type: STREAM_CREATION_ACTIONS.CHANGE_SOURCE_VALUE,
        sourceValue: cleanSourceValue,
      };
      dispatch(changeSourceValueAction);
    };
  },

  addExclusionSource:
    () => async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      if (getState().streams.streamCreation.hasOutstandingSaveRequest) {
        return;
      }

      // TODO: Use a better name than "SaveRequest" to describe what is happening here
      dispatch(streamCreationActions.setOutstandingSaveRequest(true));

      if (getState().streams.streamCreation.errors.exclusionSourceError) {
        dispatch(streamCreationActions.clearExclusionSourceError());
      }

      const currentSource =
        getState().streams.streamCreation.exclusionSourceSelector;

      if (currentSource.sourceType === StreamSourceTypes.USER) {
        await addExclusionUserSource(
          dispatch,
          getState,
          currentSource.sourceValue,
        );
      } else if (currentSource.sourceType === StreamSourceTypes.HASHTAG) {
        await addExclusionHashtagSource(
          dispatch,
          getState,
          currentSource.sourceValue,
        );
      } else {
        dispatch<ISetExclusionSourceErrorAction>({
          type: STREAM_CREATION_ACTIONS.SET_EXCLUSION_SOURCE_ERROR,
          exclusionSourceError: 'You must choose a source type',
        });
      }

      dispatch(streamCreationActions.setOutstandingSaveRequest(false));
    },

  removeExclusionSource: (exclusionSource: IStreamSource) => {
    return (dispatch: ReduxDispatch) => {
      dispatch<IRemoveExclusionSourceAction>({
        type: STREAM_CREATION_ACTIONS.REMOVE_EXCLUSION_SOURCE,
        exclusionSource,
      });
    };
  },

  changeExclusionSourceValue: (sourceValue: string) => {
    return (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      if (getState().streams.streamCreation.errors.exclusionSourceError) {
        dispatch(streamCreationActions.clearExclusionSourceError());
      }

      const cleanExclusionSourceValue = sourceValue
        .replace('@', '')
        .replace('#', '');
      const changeExclusionSourceValueAction: IChangeExclusionSourceValueAction =
        {
          type: STREAM_CREATION_ACTIONS.CHANGE_EXCLUSION_SOURCE_VALUE,
          sourceValue: cleanExclusionSourceValue,
        };
      dispatch(changeExclusionSourceValueAction);
    };
  },

  setSourceError(sourceError: string): ISetSourceErrorAction {
    return {
      type: STREAM_CREATION_ACTIONS.SET_SOURCE_ERROR,
      sourceError,
    };
  },

  clearSourceError(): ISetSourceErrorAction {
    return {
      type: STREAM_CREATION_ACTIONS.SET_SOURCE_ERROR,
      sourceError: undefined,
    };
  },

  clearExclusionSourceError(): ISetExclusionSourceErrorAction {
    return {
      type: STREAM_CREATION_ACTIONS.SET_EXCLUSION_SOURCE_ERROR,
      exclusionSourceError: undefined,
    };
  },

  changeSourceType: (sourceType: StreamSourceTypes) => {
    return async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      if (getState().streams.streamCreation.errors.sourceTypeError) {
        dispatch(streamCreationActions.clearSourceTypeError());
      }

      const changeSourceTypeAction: IChangeSourceTypeAction = {
        type: STREAM_CREATION_ACTIONS.CHANGE_SOURCE_TYPE,
        sourceType,
      };
      dispatch(changeSourceTypeAction);
    };
  },

  changeExclusionSourceType: (sourceType: StreamSourceTypes) => {
    return async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      if (getState().streams.streamCreation.errors.exclusionSourceTypeError) {
        dispatch(streamCreationActions.clearExclusionSourceTypeError());
      }

      const changeExclusionSourceTypeAction: IChangeExclusionSourceTypeAction =
        {
          type: STREAM_CREATION_ACTIONS.CHANGE_EXCLUSION_SOURCE_TYPE,
          sourceType,
        };
      dispatch(changeExclusionSourceTypeAction);
    };
  },

  setSourceTypeError(sourceTypeError: string): ISetSourceTypeErrorAction {
    return {
      type: STREAM_CREATION_ACTIONS.SET_SOURCE_TYPE_ERROR,
      sourceTypeError,
    };
  },

  clearSourceTypeError(): ISetSourceTypeErrorAction {
    return {
      type: STREAM_CREATION_ACTIONS.SET_SOURCE_TYPE_ERROR,
      sourceTypeError: undefined,
    };
  },

  clearExclusionSourceTypeError(): ISetExclusionSourceTypeErrorAction {
    return {
      type: STREAM_CREATION_ACTIONS.SET_EXCLUSION_SOURCE_TYPE_ERROR,
      exclusionSourceTypeError: undefined,
    };
  },

  changeStreamName: (streamName: string) => {
    return async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      if (getState().streams.streamCreation.errors.streamNameError) {
        dispatch(streamCreationActions.clearStreamNameError());
      }

      const changeNameAction: IChangeStreamNameAction = {
        type: STREAM_CREATION_ACTIONS.CHANGE_STREAM_NAME,
        streamName,
      };
      dispatch(changeNameAction);
    };
  },

  setStreamNameError(streamNameError: string): ISetStreamNameErrorAction {
    return {
      type: STREAM_CREATION_ACTIONS.SET_STREAM_NAME_ERROR,
      streamNameError,
    };
  },

  clearStreamNameError(): ISetStreamNameErrorAction {
    return {
      type: STREAM_CREATION_ACTIONS.SET_STREAM_NAME_ERROR,
      streamNameError: undefined,
    };
  },

  setCreateStreamError(createStreamError: string): ISetCreateStreamErrorAction {
    return {
      type: STREAM_CREATION_ACTIONS.SET_CREATE_STREAM_ERROR,
      createStreamError,
    };
  },

  clearCreateStreamError(): ISetCreateStreamErrorAction {
    return {
      type: STREAM_CREATION_ACTIONS.SET_CREATE_STREAM_ERROR,
      createStreamError: undefined,
    };
  },

  createOrUpdateStream:
    () => async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      dispatch(streamCreationActions.setOutstandingSaveRequest(true));

      if (getState().streams.streamCreation.errors.createStreamError) {
        dispatch(streamCreationActions.clearCreateStreamError());
      }

      const streamCreation = getState().streams.streamCreation;

      const sourcesAdded = streamCreation.sourcesAdded;
      const instagramHashtags = sourcesAdded
        .filter((src) => src.sourceType === StreamSourceTypes.HASHTAG)
        .map((src) => src.sourceValue);

      const instagramBusinessUsers = sourcesAdded
        .filter((src) => src.sourceType === StreamSourceTypes.USER)
        .map((src) => src.sourceValue);

      const businessUserMappings = sourcesAdded
        .filter((src) => src.sourceType === StreamSourceTypes.USER)
        .reduce((memo, src) => {
          memo[src.sourceValue] = src.sourceLabel;
          return memo;
        }, {});

      const shouldIncludeMentions = sourcesAdded.some((src) => {
        return src.sourceType === StreamSourceTypes.MENTIONS_AND_TAGS;
      });

      const isMultiHandleFeatureEnabled =
        getHasMultiHandleInstagramEnabled(getState());

      const accountInstagramId = isMultiHandleFeatureEnabled
        ? [getMultiHandleAccountsUserId(getState())]
        : [getBrandInstagramId(getState())];

      const sources: ICollectionSourcesDto = {
        businessUserMappings,
        instagramBusinessUsers,
        instagramHashtags,
        instagramMentions: shouldIncludeMentions ? accountInstagramId : [],
      };

      const exclusionSourcesAdded = streamCreation.exclusionSourcesAdded;

      const excludedContentFilters = exclusionSourcesAdded.map(
        (source: IStreamSource) => {
          return {
            network: 'Instagram',
            contentType:
              source.sourceType === StreamSourceTypes.USER
                ? StreamExcludedContentSourceTypes.USERNAME
                : StreamExcludedContentSourceTypes.HASHTAG,
            value: source.sourceValue,
          };
        },
      );

      const filters: ICollectionFiltersDto = {
        spam: true,
        excludedContentSources: excludedContentFilters,
      };

      const existingStreamId = streamCreation?.existingStream?.id;

      try {
        const stream = existingStreamId
          ? await collectionsApi.updateStream(
              existingStreamId,
              streamCreation.streamName,
              sources,
              filters,
            )
          : await collectionsApi.createStream(
              streamCreation.streamName,
              sources,
              filters,
            );

        const streams = await collectionsApi.getStreams();

        const setStreamsAction: ISetStreamsAction = {
          type: StreamsDrawerActionNames.SET_STREAMS,
          streams,
        };
        dispatch(setStreamsAction);

        // Forces reload of stream and filters. Required for both new stream creation and stream update
        // actions to effect the content grid
        dispatch(
          getStreamsDrawerActions().selectStream(stream as IStream, true),
        );
      } catch (error) {
        let createStreamError = '';
        const err = getRequestErrorMessage(error);

        if (err) {
          if (err.includes('TrackLimit::hashtag')) {
            createStreamError =
              'You have added too many hashtags. Please remove some and try again.';
          } else if (err.includes('TrackLimit::business-user')) {
            createStreamError =
              'You have added too many users. Please remove some and try again.';
          } else {
            createStreamError = `Error creating/editing stream.`;
          }
        }

        dispatch(streamCreationActions.setCreateStreamError(createStreamError));
      }

      await setTrackingSourceTimeout(dispatch);

      dispatch(streamCreationActions.setOutstandingSaveRequest(false));
    },

  closeNewStreamModal: () => (dispatch: ReduxDispatch) => {
    dispatch({
      type: StreamsDrawerActionNames.CLOSE_STREAM_MODAL,
      modalType: StreamModalType.STANDARD,
    });

    const clearStateAction: IClearStateAction = {
      type: STREAM_CREATION_ACTIONS.CLEAR_STATE,
    };
    dispatch(clearStateAction);
  },

  loadExistingSourcesState:
    () => async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const existingCollection =
        getState().streams.streamCreation.existingStream;

      if (existingCollection) {
        const changeStreamNameAction: IChangeStreamNameAction = {
          type: STREAM_CREATION_ACTIONS.CHANGE_STREAM_NAME,
          streamName: existingCollection.name,
        };
        dispatch(changeStreamNameAction);

        await trackedSourcesApi.getAllTrackedSources();

        const streamSources = streamsConversions.convertStreamSourcesToSources(
          existingCollection.sources,
          existingCollection.sourcesMetadata,
        );

        if (!_(streamSources).isEmpty()) {
          const setSelectedSourcesAction: ISetSelectedSourcesAction = {
            type: STREAM_CREATION_ACTIONS.SET_SELECTED_SOURCES,
            sources: streamSources,
          };
          dispatch(setSelectedSourcesAction);
        }

        const streamExcludedSources =
          streamsConversions.convertStreamExcludedSourcesToExcludedSources(
            existingCollection.filters,
          );

        if (streamExcludedSources) {
          dispatch<ISetSelectedExclusionSourcesAction>({
            type: STREAM_CREATION_ACTIONS.SET_SELECTED_EXCLUDED_SOURCES,
            exclusionSources: streamExcludedSources,
          });
        }
      }

      const isDoneLoadingSourcesAction: ISetIsLoadingSourcesAction = {
        type: STREAM_CREATION_ACTIONS.SET_IS_LOADING_SOURCES,
        isLoadingSources: false,
      };
      dispatch(isDoneLoadingSourcesAction);
    },

  loadShareStream:
    () => async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const streams = await collectionsApi.getStreams();

      const defaultStreams = _(streams).filter(
        (stream) => stream.name === DEFAULT_STREAM_NAME,
      );

      if (defaultStreams) {
        const action: ISetExistingStreamAction = {
          type: STREAM_CREATION_ACTIONS.SET_EXISTING_STREAM,
          existingStream: defaultStreams[0] as IStream,
        };
        dispatch(action);

        if (defaultStreams.length > 1) {
          const clientId = getState().session.client.id;
          throw new Error(
            `More than one stream found for clientId: ${clientId}`,
          );
        }
      }
    },

  updateStreamName:
    (newStreamName: string) =>
    async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      const selectedStream = getState().streams.streamsDrawer.selectedStream;

      const stream = (await collectionsApi.updateStream(
        selectedStream.id,
        newStreamName,
        selectedStream.sources,
        selectedStream.filters,
      )) as IStream;

      const selectStreamAction: ISelectStreamAction = {
        type: StreamsDrawerActionNames.SELECT_STREAM,
        stream,
      };
      dispatch(selectStreamAction);

      dispatch(setCurrentStreamName(newStreamName));
    },
};
